import React, { useState, useRef } from 'react';
import { api } from '../services/api';
import './SpeechToTextPage.css';

export const SpeechToTextPage: React.FC = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [transcription, setTranscription] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [jobName, setJobName] = useState<string>('');
  const [jobStatus, setJobStatus] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
        setAudioBlob(audioBlob);
      };

      mediaRecorder.start();
      setIsRecording(true);
      setError('');
    } catch (err) {
      setError('Failed to start recording. Please check microphone permissions.');
      console.error('Error starting recording:', err);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      setIsRecording(false);
    }
  };

  const transcribeAudio = async () => {
    if (!audioBlob) {
      setError('No audio recorded. Please record audio first.');
      return;
    }

    setIsProcessing(true);
    setError('');

    try {
      // Convert blob to base64 using FileReader for better memory efficiency
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          // Remove the data URL prefix (e.g., "data:audio/wav;base64,")
          const base64Data = result.split(',')[1];
          resolve(base64Data);
        };
        reader.onerror = () => reject(new Error('Failed to read audio file'));
        reader.readAsDataURL(audioBlob);
      });

      const response = await api.transcribeAudio(base64, 'en-US');

      setJobName(response.jobName);
      setJobStatus(response.status);
      
      // Start polling for job status
      pollJobStatus(response.jobName);
    } catch (err) {
      setError('Failed to transcribe audio. Please try again.');
      console.error('Error transcribing audio:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  const pollJobStatus = async (jobName: string) => {
    const pollInterval = setInterval(async () => {
      try {
        const status = await api.getTranscriptionJobStatus(jobName);
        
        setJobStatus(status.status);
        
        if (status.status === 'COMPLETED') {
          clearInterval(pollInterval);
          // Fetch the transcript using the job name
          await fetchTranscript(jobName);
        } else if (status.status === 'FAILED') {
          clearInterval(pollInterval);
          setError(status.errorMessage || 'Transcription failed');
        }
      } catch (err) {
        console.error('Error polling job status:', err);
        clearInterval(pollInterval);
      }
    }, 2000); // Poll every 2 seconds
  };

  const fetchTranscript = async (jobName: string) => {
    try {
      const transcriptData = await api.getTranscript(jobName);
      const transcript = transcriptData.results?.transcripts?.[0]?.transcript || '';
      setTranscription(transcript);
    } catch (err) {
      console.error('Error fetching transcript:', err);
      setError('Failed to fetch transcript');
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setError('');
    }
  };

  const transcribeFile = async () => {
    if (!selectedFile) {
      setError('Please select a file first.');
      return;
    }

    setIsProcessing(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await api.transcribeFile(selectedFile);

      setJobName(response.jobName);
      setJobStatus(response.status);
      
      // Start polling for job status
      pollJobStatus(response.jobName);
    } catch (err) {
      setError('Failed to transcribe file. Please try again.');
      console.error('Error transcribing file:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  const clearResults = () => {
    setTranscription('');
    setAudioBlob(null);
    setJobName('');
    setJobStatus('');
    setSelectedFile(null);
    setError('');
  };

  return (
    <div className="speech-to-text-page">
      <div className="container">
        <h1>Speech to Text</h1>
        <p className="subtitle">Convert speech to text using AWS Transcribe</p>

        <div className="sections">
          {/* Recording Section */}
          <div className="section">
            <h2>Record Audio</h2>
            <div className="recording-controls">
              <button
                className={`record-button ${isRecording ? 'recording' : ''}`}
                onClick={isRecording ? stopRecording : startRecording}
                disabled={isProcessing}
              >
                {isRecording ? '⏹️ Stop Recording' : '🎤 Start Recording'}
              </button>
              
              {audioBlob && (
                <button
                  className="transcribe-button"
                  onClick={transcribeAudio}
                  disabled={isProcessing}
                >
                  {isProcessing ? '🔄 Processing...' : '📝 Transcribe Audio'}
                </button>
              )}
            </div>

            {audioBlob && (
              <div className="audio-preview">
                <audio controls src={URL.createObjectURL(audioBlob)} />
                <p>Audio recorded successfully!</p>
              </div>
            )}
          </div>

          {/* File Upload Section */}
          <div className="section">
            <h2>Upload Audio File</h2>
            <div className="file-upload">
              <input
                type="file"
                accept=".wav,.mp3,.mp4,.flac,.webm"
                onChange={handleFileUpload}
                id="audio-file"
                className="file-input"
              />
              <label htmlFor="audio-file" className="file-label">
                📁 Choose Audio File
              </label>
              
              {selectedFile && (
                <div className="file-info">
                  <p>Selected: {selectedFile.name}</p>
                  <button
                    className="transcribe-button"
                    onClick={transcribeFile}
                    disabled={isProcessing}
                  >
                    {isProcessing ? '🔄 Processing...' : '📝 Transcribe File'}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Status Section */}
          {jobName && (
            <div className="section">
              <h2>Transcription Status</h2>
              <div className="status-info">
                <p><strong>Job Name:</strong> {jobName}</p>
                <p><strong>Status:</strong> <span className={`status ${jobStatus.toLowerCase()}`}>{jobStatus}</span></p>
                {jobStatus === 'IN_PROGRESS' && (
                  <div className="loading-spinner">🔄 Processing...</div>
                )}
              </div>
            </div>
          )}

          {/* Results Section */}
          {transcription && (
            <div className="section">
              <h2>Transcription Results</h2>
              <div className="transcription-results">
                <textarea
                  value={transcription}
                  readOnly
                  placeholder="Transcription will appear here..."
                  className="transcription-text"
                />
                <button
                  className="copy-button"
                  onClick={() => navigator.clipboard.writeText(transcription)}
                >
                  📋 Copy to Clipboard
                </button>
              </div>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="error-message">
              ❌ {error}
            </div>
          )}

          {/* Clear Results */}
          {(transcription || audioBlob || selectedFile) && (
            <div className="section">
              <button className="clear-button" onClick={clearResults}>
                🗑️ Clear All Results
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}; 